import React from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { features } from "../../constants/featuresData";
import OrangeHighlightButton from "../../elements/OrangeHighlightButton";

export interface WhyUsSectionProps {
  section: "home" | "nursery";
}

const WhyUsSection: React.FC<WhyUsSectionProps> = ({ section }) => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || "en";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isNursery = section === "nursery";

  const backgroundColor = isNursery ? theme.palette.primary.main : "#FFF8F1";
  const textColor = theme.palette.text.primary;

  const featuresToShow = isNursery
    ? [features[0], features[1], features[2], features[4]]
    : features;

  const handleRedirection = () => {
    const path =
      currentLanguage === "en"
        ? isNursery
          ? "enroll"
          : "approach"
        : `/${currentLanguage}/${isNursery ? "enroll" : "approach"}`;
    history.push(path);
  };

  return (
    <Box
      sx={{
        backgroundColor,
        px: isMobile ? "20px" : "10%",
        py: isNursery && isMobile ? "4vh" : "10vh",
        pb: isNursery && isMobile ? "10vh" : 0,
        mt: isNursery ? 0 : "10vh",
        mb: "5vh",
      }}
    >
      <Grid container direction="row" spacing={6}>
        <Grid
          item
          xs={12}
          md={4}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems={isMobile ? "center" : "start"}
        >
          <Typography
            variant="h2"
            fontWeight="800"
            textAlign={isMobile ? "center" : "start"}
            color={theme.palette.text.secondary}
            mb={4}
          >
            {t(`WhyUsSection.${isNursery ? "titleNursery" : "titleHome"}`)}
          </Typography>
          <Typography
            variant="h5"
            fontWeight={400}
            textAlign={isMobile ? "center" : "start"}
            color={textColor}
            maxWidth={800}
            mx="auto"
          >
            {t("WhyUsSection.text")}
          </Typography>
          <OrangeHighlightButton sx={{ mt: 2 }} onClick={handleRedirection}>
            {t(`WhyUsSection.${isNursery ? "buttonNursery" : "buttonHome"}`)}
          </OrangeHighlightButton>
        </Grid>
        <Grid item xs={12} md={8} container spacing={4} justifyContent="center">
          {featuresToShow.map((feature, index) => (
            <Grid key={index} item xs={12} md={isNursery ? 6 : 4}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  px: 2,
                }}
              >
                <Box
                  component="img"
                  src={`/icons/${feature.icon}.png`}
                  alt={t(`WhyUsSection.${feature.titleKey}`)}
                  sx={{ width: 116, height: 116, mb: 2 }}
                />
                <Typography variant="h5" fontWeight={600} color={textColor}>
                  {t(`WhyUsSection.${feature.titleKey}`)}
                </Typography>
                <Typography variant="body1" color={textColor}>
                  {t(`WhyUsSection.${feature.descriptionKey}`)}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Box>
  );
};

export default WhyUsSection;
